import type { CandidateResume } from '@entities/candidates';
import { formatCurrencyPaymentPeriod } from '@shared/helpers';
import type { CandidateResumeDisplayValues } from '../types';

const EMPTY_VALUE = '-';

type NamedReference = {
  name: string;
};

const formatReferenceNames = (references: NamedReference[]) => {
  const names = references
    .map(reference => reference.name.trim())
    .filter(Boolean);

  return names.length > 0 ? names.join(', ') : EMPTY_VALUE;
};

const formatScalar = (value: string | null) => {
  const normalizedValue = value?.trim();

  return normalizedValue || EMPTY_VALUE;
};

const formatSalary = (
  resume: Pick<CandidateResume, 'currency' | 'paymentPeriod' | 'salary'>
) => {
  if (resume.salary === null) {
    return EMPTY_VALUE;
  }

  const currency =
    resume.currency?.value.trim() || resume.currency?.code.trim() || '';
  const paymentPeriod = resume.paymentPeriod?.name.trim();
  const formattedPeriod = formatCurrencyPaymentPeriod(currency, paymentPeriod);

  return `${resume.salary} ${formattedPeriod}`.trim();
};

export const formatCandidateResume = (
  resume: CandidateResume
): CandidateResumeDisplayValues => ({
  profession: formatReferenceNames(resume.activitySpheres),
  desiredJob: formatScalar(resume.desiredJob),
  skills: formatReferenceNames(resume.skills),
  experience: formatScalar(resume.experience),
  salary: formatSalary(resume),
  scheduleWorks: formatReferenceNames(resume.scheduleWorks),
  employmentTypes: formatReferenceNames(resume.employmentTypes),
});
